import { Button, Group, Modal, Stack, Text } from '@mantine/core'
import { IconCamera, IconRefresh } from '@tabler/icons-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const VIDEO_SIZE = 320
const ICON_SIZE = 16

interface CameraCaptureProps {
  opened: boolean
  onClose: () => void
  onCapture: (base64: string) => void
}

/**
 * Modal that opens the device camera, shows a live preview,
 * and lets the user capture a snapshot for their avatar.
 */
export function CameraCapture({ opened, onClose, onCapture }: CameraCaptureProps) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop())
    streamRef.current = null
  }, [])

  useEffect(() => {
    if (!opened) {
      stopStream()
      setPreview(null)
      setError(null)
      return
    }

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: VIDEO_SIZE, height: VIDEO_SIZE },
        })
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
      } catch (err: any) {
        const key = err.name === 'NotAllowedError'
          ? 'my_profile.camera_permission_denied'
          : 'my_profile.camera_not_available'
        setError(t(key))
      }
    }

    startCamera()
    return stopStream
  }, [opened, stopStream, t])

  function handleCapture() {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')?.drawImage(video, 0, 0)
    setPreview(canvas.toDataURL('image/png'))
    stopStream()
  }

  function handleRetake() {
    setPreview(null)
    // Re-trigger camera by toggling opened effect
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: VIDEO_SIZE, height: VIDEO_SIZE },
        })
        streamRef.current = stream
        if (videoRef.current) videoRef.current.srcObject = stream
      } catch { /* error already handled on first open */ }
    }
    startCamera()
  }

  function handleUsePhoto() {
    if (preview) {
      onCapture(preview)
      onClose()
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('my_profile.take_picture')} centered size='sm'>
      <Stack align='center' spacing='md'>
        {error ? (
          <Text color='red' size='sm'>{error}</Text>
        ) : (
          <>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              width={VIDEO_SIZE}
              height={VIDEO_SIZE}
              style={{ display: preview ? 'none' : 'block', borderRadius: 8 }}
            />
            {preview && (
              <img src={preview} alt='Captured' width={VIDEO_SIZE} height={VIDEO_SIZE} style={{ borderRadius: 8 }} />
            )}
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            <Group>
              {preview ? (
                <>
                  <Button variant='light' size='sm' leftIcon={<IconRefresh size={ICON_SIZE} />} onClick={handleRetake}>
                    {t('my_profile.retake')}
                  </Button>
                  <Button variant='filled' color='turquoise' size='sm' onClick={handleUsePhoto}>
                    {t('my_profile.use_photo')}
                  </Button>
                </>
              ) : (
                <Button variant='filled' color='turquoise' size='sm' leftIcon={<IconCamera size={ICON_SIZE} />} onClick={handleCapture}>
                  {t('my_profile.capture')}
                </Button>
              )}
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  )
}
